import React, { useState, useEffect } from "react";
import {
  Shield,
  Lock,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../../utils/axiosConfig";
import "./payment.css";

const PaymentPage = ({
  selectedAddress,
  totalItems,
  totalAmount,
  deliveryCharge,
  getTaxBreakdown,
  getDiscountAmount,
  calculateFinalTotal,
  discount,
  finalTotal,
  selectedCoupon, // 👈 Must be object: { code, discountPercentage }
  cartItems,
  onBackToAddress,
  onPaymentFailure,
}) => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState("razorpay");
  const [razorpayInstance, setRazorpayInstance] = useState(null);

  // Price Calculations
  const discountAmount =
    typeof getDiscountAmount === "function"
      ? getDiscountAmount()
      : discount || 0;
  const totalPayable =
    typeof calculateFinalTotal === "function"
      ? calculateFinalTotal()
      : finalTotal || totalAmount;
  const taxBreakdown =
    typeof getTaxBreakdown === "function" ? getTaxBreakdown() : {};

  // Razorpay Script Loader
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
      if (razorpayInstance) razorpayInstance.close();
    };
  }, [razorpayInstance]);

  // 🧾 Create Order + Payment
  const handleRazorpayPayment = async () => {
    if (!selectedAddress) {
      alert("Please select a delivery address first.");
      return;
    }
    if (!accessToken) {
      alert("Please login to continue with payment.");
      return;
    }

    setIsProcessing(true);

    try {
      // 1️⃣ Create Order
      const orderData = {
        amount: totalPayable,
        currency: "INR",
        receipt: `receipt_${Date.now()}`,
        notes: {
          address: selectedAddress._id,
          items: totalItems,
          deliveryCharge: deliveryCharge,
          discount: discountAmount,

          // 👇 SEND CORRECT COUPON DATA
          coupon: selectedCoupon
            ? {
                code: selectedCoupon.code, // e.g., "WELCOME10"
                discountPercentage: selectedCoupon.discountPercentage, // e.g., 10
              }
            : null,

          taxAmount: Object.values(taxBreakdown).reduce(
            (sum, tax) => sum + tax,
            0
          ),
          taxBreakdown: taxBreakdown,
        },
      };

      const response = await api.post("/payment/create-order", orderData);
      const data = response.data;
      if (!data.success) throw new Error(data.message);

      // 2️⃣ Razorpay Payment
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_RZxN2wkegiCDjz",
        amount: data.order.amount,
        currency: data.order.currency,
        name: "Shivnari Fresh",
        description: `Order for ${totalItems} items`,
        image: "/logo.png",
        order_id: data.order.id,

        prefill: {
          name: selectedAddress.fullName,
          email: selectedAddress.email,
          contact: selectedAddress.contactNumber,
        },

        theme: { color: "#3399cc" },

        // 3️⃣ PAYMENT SUCCESS
        handler: async function (response) {
          try {
            const verifyResponse = await api.post("/payment/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,

              orderDetails: {
                items: cartItems,
                address: selectedAddress,
                amount: totalPayable,
                deliveryCharge: deliveryCharge,
                discount: discountAmount,

                // 👇 SEND COUPON AGAIN
                coupon: selectedCoupon
                  ? {
                      code: selectedCoupon.code,
                      discountPercentage: selectedCoupon.discountPercentage,
                    }
                  : null,

                taxBreakdown: taxBreakdown,
              },
            });

            const verifyData = verifyResponse.data;
            if (verifyData.success) {
              setPaymentStatus("success");
              setIsProcessing(false);

              setTimeout(() => {
                navigate("/myorder", {
                  state: {
                    paymentSuccess: true,
                    orderId: verifyData.orderId,
                    orderData: verifyData,
                  },
                });
              }, 1400);
            } else throw new Error("Payment verification failed");
          } catch (error) {
            setPaymentStatus("failed");
            setIsProcessing(false);
            alert("Payment verification failed. Please try again.");
          }
        },

        // ❌ Payment Closed or Failed
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
            console.log("Payment modal closed by user");
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      setRazorpayInstance(razorpay);

      razorpay.on("payment.failed", function (response) {
        setPaymentStatus("failed");
        setIsProcessing(false);
        alert(
          `Payment failed: ${response.error.description || "Please try again."}`
        );
      });

      razorpay.open();
    } catch (error) {
      alert(
        error.response?.data?.message ||
          error.message ||
          "Payment initialization failed."
      );
      setIsProcessing(false);
      setPaymentStatus("failed");
    }
  };

  // 🎉 SUCCESS UI
  if (paymentStatus === "success") {
    return (
      <div className="payment-container">
        <div className="success-card">
          <CheckCircle size={80} color="#10b981" />
          <h1 className="success-title">Payment Successful!</h1>
          <p className="success-message">
            Your order has been placed successfully.
          </p>
          <div className="success-details">
            <p>
              <strong>Amount Paid:</strong> ₹{totalPayable.toLocaleString()}
            </p>
            <p>
              <strong>Items:</strong> {totalItems}
            </p>
          </div>
          <button
            className="primary-button"
            onClick={() => navigate("/myorder")}
          >
            View Orders
          </button>
          <button className="secondary-button" onClick={() => navigate("/")}>
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  // 💥 FAILED UI
  if (paymentStatus === "failed") {
    return (
      <div className="payment-container">
        <div className="failure-card">
          <AlertCircle size={80} color="#ef4444" />
          <h1 className="failure-title">Payment Failed</h1>
          <p className="failure-message">
            Your payment could not be processed. Try again.
          </p>
          <div className="button-group">
            <button
              className="primary-button"
              onClick={() => {
                setPaymentStatus(null);
                setIsProcessing(false);
              }}
            >
              Try Again
            </button>
            <button className="secondary-button" onClick={onBackToAddress}>
              Back to Address
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 🧾 MAIN PAYMENT UI
  return (
    <div className="payment-container">
      <div className="payment-page">
        <div className="payment-header">
          <h1 className="page-title">Complete Your Payment</h1>
          <p className="page-subtitle">
            Review your order and proceed securely
          </p>
        </div>

        <div className="payment-layout">
          <div className="left-section">
            <div className="card">
              <h2 className="card-title">Delivery Address</h2>
              <div className="address-box">
                <p className="address-name">{selectedAddress.fullName}</p>
                <p className="address-text">{selectedAddress.address}</p>
                <p className="address-text">
                  {selectedAddress.landmark && `${selectedAddress.landmark}, `}
                  {selectedAddress.state} - {selectedAddress.pincode}
                </p>
                <p className="address-contact">
                  Phone: {selectedAddress.contactNumber}
                </p>
                <p className="address-contact">
                  Email: {selectedAddress.email}
                </p>
              </div>
            </div>

            <div className="card">
              <h2 className="card-title">Payment Method</h2>

              <div
                className={`payment-option ${
                  selectedPaymentMethod === "razorpay"
                    ? "payment-option-selected"
                    : ""
                }`}
                onClick={() => setSelectedPaymentMethod("razorpay")}
              >
                <div className="payment-option-content">
                  <CreditCard size={24} />
                  <div>
                    <p className="payment-option-title">Razorpay</p>
                    <p className="payment-option-desc">
                      Card, UPI, Net Banking & Wallets
                    </p>
                  </div>
                </div>
                <div
                  className={
                    selectedPaymentMethod === "razorpay"
                      ? "radio-selected"
                      : "radio"
                  }
                ></div>
              </div>

              <div className="security-badges">
                <div className="badge">
                  <Shield size={16} />
                  <span>100% Secure</span>
                </div>
                <div className="badge">
                  <Lock size={16} />
                  <span>Encrypted</span>
                </div>
              </div>
            </div>

            <button
              className={`pay-button ${
                isProcessing ? "pay-button-disabled" : ""
              }`}
              onClick={handleRazorpayPayment}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader size={20} className="spinner" /> Processing...
                </>
              ) : (
                <>
                  <Lock size={20} /> Pay ₹{totalPayable.toLocaleString()}
                </>
              )}
            </button>

            <p className="payment-note">
              By clicking "Pay", you agree to our terms and conditions.
            </p>
          </div>

          {/* SUMMARY */}
          <div className="right-section">
            <div className="summary-card">
              <h2 className="summary-title">Order Summary</h2>

              <div className="summary-details">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>₹{totalAmount.toLocaleString()}</span>
                </div>

                <div className="summary-row">
                  <span>Delivery Fee</span>
                  <span className={deliveryCharge === 0 ? "free-text" : ""}>
                    {deliveryCharge === 0
                      ? "Free"
                      : `₹${deliveryCharge.toLocaleString()}`}
                  </span>
                </div>

                {Object.entries(taxBreakdown).map(([gstRate, taxAmount]) => (
                  <div key={gstRate} className="summary-row">
                    <span>GST ({gstRate}%)</span>
                    <span>₹{Math.round(taxAmount).toLocaleString()}</span>
                  </div>
                ))}

                {discountAmount > 0 && selectedCoupon && (
                  <div className="summary-row coupon-applied-payment">
                    <span>
                      Coupon Applied ({/* ✅ FIX: Render the code property */}
                      {typeof selectedCoupon === "object"
                        ? selectedCoupon.code
                        : selectedCoupon}
                      )
                    </span>
                    <span className="discount-text">
                      -₹{discountAmount.toLocaleString()}
                    </span>
                  </div>
                )}

                <hr className="payment-divider" />

                <div className="summary-total">
                  <span>Total After Discount</span>
                  <span className="total-amount">
                    ₹{totalPayable.toLocaleString()}
                  </span>
                </div>

                {/* {discountAmount > 0 && (
                  <div className="savings-info">
                    You're saving ₹{discountAmount.toLocaleString()} on this
                    order! 🎉
                  </div>
                )} */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
